import React from 'react';
import { useRecordContext } from 'react-admin';
import { Grid, useMediaQuery } from '@material-ui/core';
import { AssignmentIndOutlined } from '@material-ui/icons';
import { Redirect } from 'react-router-dom';

import { FF_DRAFT_GAMES, FF_SCOREKEEPERS } from '@hisports/common/featureFlags';

import { isAuthorized } from '../../common/Authorize';
import { Tab, TabbedShowLayout } from '../../common/ra/TabbedShowLayout';
import { useFlag } from '../../http';
import SchedulerView from '../../common/calendar/schedulers/league/SchedulerView';

import SchedulesCard from '../offices/cards/SchedulesCard';
import AvailabilityCard from '../availabilities/AvailabilityCard';
import GroupsCard from '../offices/cards/GroupsCard';
import SchedulingConstraintsCard from '../offices/cards/SchedulingConstraintsCard';
import RolesCard from '../offices/cards/RolesCard';
import NotificationCard from '../offices/cards/NotificationsCard';
import AssignRulesCard from '../offices/cards/AssignRulesCard';
import SuspensionsCard from '../leagues/cards/SuspensionsCard';
import GamePenaltiesCard from '../leagues/cards/GamePenaltiesCard';
import OfficeSettingsCard from '../offices/cards/OfficeSettingsCard';
import ExternalProvidersCard from '../externalProviders/ExternalProvidersCard';
import OfficeActionsCard from '../offices/cards/OfficeActionsCard';
import ClaimsSettingsCard from '../offices/cards/ClaimsSettingsCard';
import PayPeriodsCard from '../offices/cards/PayPeriodsCard';
import ClaimsCard from '../offices/cards/ClaimsCard';
import OfficialsCard from '../offices/cards/OfficialsCard';
import ListsCard from '../offices/cards/ListsCard';
import AttributeTypesCard from '../offices/cards/AttributeTypesCard';
import AssignSystemsCard from '../offices/cards/AssignSystemsCard';
import AssignFeesCard from '../offices/cards/AssignFeesCard';
import AssignTargetsCard from '../offices/cards/AssignTargetsCard';
import OfficeAssignSettingsCard from '../offices/cards/OfficeAssignSettingsCard';
import ArenasCard from '../offices/cards/ArenasCard';

const SummaryTab = props => {
  const office = useRecordContext(props);

  const showSchedules = isAuthorized(office, 'schedules', 'list')
  const showGamePenalties = isAuthorized(office, 'gamepenalties', 'show')
  const showSuspensions = isAuthorized(office, 'suspensions', 'show')
  const showExternalProviders = isAuthorized(office, 'tournaments', 'list');

  const showAvailabilitiesCard = isAuthorized(office, 'availabilities', 'list')
  const canEditAvailabilities = isAuthorized(office, 'availabilities', 'edit')
  const showArenas = isAuthorized(office, 'surfaces', 'list');

  return <Tab {...props}>
    <Grid container spacing={3}>
      {showSchedules && <Grid item xs={12} md={8} >
        <SchedulesCard showOffice={false} />
      </Grid>}
      <Grid item xs={12} md={4} >
        <OfficeActionsCard />
      </Grid>
      {showAvailabilitiesCard && <Grid item xs={12}>
        <AvailabilityCard collapsable editable={canEditAvailabilities} />
      </Grid>}
      {showGamePenalties && <Grid item xs={12}>
        <GamePenaltiesCard collapsable />
      </Grid>}
      {showSuspensions && <Grid item xs={12}>
        <SuspensionsCard collapsable />
      </Grid>}
      {showArenas && <Grid item xs={12}>
        <ArenasCard collapsable />
      </Grid>}
      {showExternalProviders && <Grid item xs={12}>
        <ExternalProvidersCard targetId={office?.id} />
      </Grid>}
    </Grid>
  </Tab>
}

const SchedulerTab = props => {
  const office = useRecordContext()
  const isEnabled = useFlag()
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('xs'));

  const showScheduler = isAuthorized(office, 'offices', 'schedulerTab');

  if (!isEnabled(FF_DRAFT_GAMES) || isMobile || !showScheduler) return null;

  return <Tab {...props}>
    <SchedulerView office={office} />
  </Tab>
}

const AssigningTab = props => {
  const office = useRecordContext(props);
  const isEnabled = useFlag();

  if (!isAuthorized(office, 'offices', 'assigningTab')) return null;

  const showAssignSettings = isAuthorized(office, 'officeAssignSettings', 'list')
  const showClaimSettings = isAuthorized(office, 'officeClaimsSettings', 'list')
  const showPayPeriods = isAuthorized(office, 'payPeriods', 'list')
  const showClaimPeriods = isAuthorized(office, 'officialClaims', 'list')
  const showOfficials = isAuthorized(office, 'officialOffices', 'list')
  const showScorekeepers = showOfficials && isEnabled(FF_SCOREKEEPERS);
  const showLists = isAuthorized(office, 'lists', 'list')
  const showAssignSystems = isAuthorized(office, 'assignSystems', 'list')
  const showAssignFees = isAuthorized(office, 'assignFees', 'list')
  const showAssignRules = isAuthorized(office, 'assignRules', 'list')
  const showAssignTargets = isAuthorized(office, 'assignTargets', 'list')
  const showAttributes = isAuthorized(office, 'attributeTypes', 'list')

  return <Tab {...props}>
    <Grid container spacing={3}>
      {showAssignSettings && <Grid item xs={12} md={6}>
        <OfficeAssignSettingsCard />
      </Grid>}
      {showClaimSettings && <Grid item xs={12}>
        <ClaimsSettingsCard />
      </Grid>}
      {showPayPeriods && <Grid item xs={12}>
        <PayPeriodsCard />
      </Grid>}
      {showClaimPeriods && <Grid item xs={12}>
        <ClaimsCard />
      </Grid>}
      {showOfficials && <Grid item xs={12}>
        <OfficialsCard type="Official" />
      </Grid>}
      {showScorekeepers && <Grid item xs={12}>
        <OfficialsCard type="Scorekeeper" />
      </Grid>}
      {showLists && <Grid item xs={12}>
        <ListsCard />
      </Grid>}
      {showAttributes && <Grid item xs={12}>
        <AttributeTypesCard type="Officiating" targetType="Participant" />
      </Grid>}
      {showAssignSystems && <Grid item xs={12}>
        <AssignSystemsCard />
      </Grid>}
      {showAssignFees && <Grid item xs={12}>
        <AssignFeesCard />
      </Grid>}
      {showAssignRules && <Grid item xs={12}>
        <AssignRulesCard
          title="resources.assignRules.labels.card.title"
          subtitle="resources.assignRules.labels.card.subtitle"
          types={["minReferee", "minLinesperson", "minOfficial", "minScorekeeper", "minTimekeeper", "minRefereeGrade", "minLinespersonGrade", "minAge", "status"]} />
      </Grid>}
      {showAssignRules && <Grid item xs={12}>
        <AssignRulesCard
          title="resources.assignRules.labels.alt_card.title"
          subtitle="resources.assignRules.labels.alt_card.subtitle"
          icon={<AssignmentIndOutlined />}
          types={["officeId", "assignType"]} />
      </Grid>}
      {showAssignTargets && <Grid item xs={12}>
        <AssignTargetsCard />
      </Grid>}
    </Grid>
  </Tab>
}

const SettingsTab = props => {
  const office = useRecordContext(props);

  if (!isAuthorized(office, 'offices', 'settingsTab')) return null;

  const showGroups = isAuthorized(office, 'groups', 'list')
  const canShowConstraints = isAuthorized(office, 'schedulingConstraints', 'list')
  const showRoles = isAuthorized(office, 'roles', 'list')
  const showNotifications = isAuthorized(office, 'notifications', 'list')
  const showAssignRules = isAuthorized(office, 'assignRules', 'list')
  const showSettings = isAuthorized(office, 'officeSettings', 'list')

  return <Tab {...props}>
    <Grid container spacing={3}>
      {showSettings && <Grid item xs={12} md={6}>
        <OfficeSettingsCard />
      </Grid>}
      {showGroups && <Grid item xs={12}>
        <GroupsCard />
      </Grid>}
      {canShowConstraints && <Grid item xs={12}>
        <SchedulingConstraintsCard scheduleTypes={office.type} />
      </Grid>}
      {showAssignRules && <Grid item xs={12}>
        <AssignRulesCard
          title="resources.assignRules.labels.alt_card.title"
          subtitle="resources.assignRules.labels.alt_card.subtitle"
          icon={<AssignmentIndOutlined />}
          types={["officeId", "assignType"]} />
      </Grid>}
      {showRoles && <Grid item xs={12}>
        <RolesCard />
      </Grid>}
      {showNotifications && <Grid item xs={12}>
        <NotificationCard />
      </Grid>}
    </Grid>
  </Tab>
}

export const TournamentShowLayout = props => {
  const office = useRecordContext(props);

  if (office?.type !== 'Tournament') return <Redirect to={`/offices/${office?.id}/show`} />
  return <TabbedShowLayout>
    <SummaryTab label="resources.offices.labels.tabs.summary" />
    <SchedulerTab label="resources.offices.labels.tabs.scheduler" path="scheduler" />
    <AssigningTab label="resources.offices.labels.tabs.assigning" path="assigning" />
    <SettingsTab label="resources.offices.labels.tabs.settings" path="settings" />
  </TabbedShowLayout>
}
